<template>
  <section class="nd-dashboard">
    <header class="nd-dashboard-toolbar">
      <div class="dashboard-range">
        <span>Período analisado</span>
        <strong>{{ rangeLabel }}</strong>
      </div>

      <div class="nd-dashboard-actions">
        <div class="period-switch">
          <button
            v-for="option in periodOptions"
            :key="option.value"
            type="button"
            :class="{ active: filters.period === option.value }"
            @click="selectPeriod(option.value)"
          >
            {{ option.label }}
          </button>
        </div>

        <div class="date-fields">
          <input v-model="filters.startDate" type="date" @change="applyCustomPeriod" />
          <input v-model="filters.endDate" type="date" @change="applyCustomPeriod" />
        </div>

        <v-btn color="primary" elevation="0" class="refresh-btn" :loading="loading" @click="loadDashboard">
          <v-icon left small>mdi-refresh</v-icon>
          Atualizar
        </v-btn>
      </div>
    </header>

    <v-alert v-if="error" type="error" outlined dense class="mb-5">
      {{ error }}
    </v-alert>

    <div class="metric-grid">
      <article v-for="metric in metrics" :key="metric.key" class="metric-card">
        <span class="metric-card__icon" :class="`tone-${metric.tone}`">
          <v-icon size="22">{{ metric.icon }}</v-icon>
        </span>
        <div>
          <small>{{ metric.label }}</small>
          <strong>{{ metric.value }}</strong>
          <span :class="['metric-card__trend', metric.variation >= 0 ? 'positive' : 'negative']">
            <v-icon size="14">{{ metric.variation >= 0 ? "mdi-arrow-up" : "mdi-arrow-down" }}</v-icon>
            {{ Math.abs(metric.variation) }}% vs. período anterior
          </span>
        </div>
      </article>
    </div>

    <v-progress-linear v-if="loading" indeterminate color="primary" height="3" class="dashboard-loader" />

    <div class="dashboard-layout">
      <div class="dashboard-main">
        <section class="dashboard-panel movement-panel">
          <div class="panel-heading">
            <div>
              <h2>Movimento da clínica</h2>
              <p>Consultas e novos pacientes no período</p>
            </div>
            <span>{{ totalAppointments }} consulta(s)</span>
          </div>
          <highcharts v-if="hasMovementData" class="chart chart-tall" :options="movementChartOptions" />
          <div v-else class="empty-chart">
            <v-icon size="30">mdi-chart-line</v-icon>
            <strong>Sem movimento no período</strong>
            <span>Quando houver consultas ou cadastros, a evolução aparece aqui.</span>
          </div>
        </section>

        <section class="dashboard-panel">
          <div class="panel-heading">
            <div>
              <h2>Procedimentos mais realizados</h2>
              <p>Ranking por volume de agenda</p>
            </div>
          </div>
          <highcharts v-if="hasProceduresData" class="chart" :options="proceduresChartOptions" />
          <div v-else class="empty-chart compact">
            <v-icon size="28">mdi-format-list-bulleted-square</v-icon>
            <strong>Nenhum procedimento ranqueado</strong>
            <span>O ranking será formado pelas consultas do período.</span>
          </div>
        </section>

        <section class="dashboard-panel">
          <div class="panel-heading">
            <div>
              <h2>Próximos atendimentos</h2>
              <p>Agenda futura ordenada por data e horário</p>
            </div>
            <v-btn text color="primary" class="panel-link" @click="$router.push('/consultas')">
              Ver agenda
              <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
          </div>

          <div v-if="upcoming.length" class="appointment-list">
            <button
              v-for="item in upcoming"
              :key="item.id"
              type="button"
              class="appointment-row"
              @click="$router.push(`/consultas/${item.id}`)"
            >
              <span class="appointment-date">
                <strong>{{ day(item.scheduledDate) }}</strong>
                <small>{{ month(item.scheduledDate) }}</small>
              </span>
              <span class="appointment-main">
                <strong>{{ item.patient }}</strong>
                <small>{{ item.procedure }} · {{ item.scheduledTime || "Horário não informado" }}</small>
              </span>
              <span class="appointment-status">{{ item.status }}</span>
            </button>
          </div>

          <div v-else class="empty-list">
            <v-icon size="30">mdi-calendar-blank-outline</v-icon>
            <span>Nenhum atendimento futuro encontrado.</span>
          </div>
        </section>
      </div>

      <aside class="dashboard-side">
        <section class="dashboard-panel">
          <div class="panel-heading">
            <div>
              <h2>Status das consultas</h2>
              <p>Distribuição operacional</p>
            </div>
          </div>
          <highcharts v-if="hasStatusData" class="chart side-chart" :options="statusChartOptions" />
          <div v-else class="empty-chart compact">
            <v-icon size="28">mdi-chart-donut</v-icon>
            <strong>Sem status para exibir</strong>
            <span>Os status serão distribuídos conforme a agenda for usada.</span>
          </div>
        </section>

        <section class="dashboard-panel">
          <div class="panel-heading">
            <div>
              <h2>Indicadores clínicos</h2>
              <p>Exames, prescrições e cadastros</p>
            </div>
          </div>

          <div class="clinical-list">
            <div class="clinical-row">
              <span>Exames pendentes</span>
              <strong>{{ number(summary.tests.pending) }}</strong>
            </div>
            <div class="clinical-row">
              <span>Exames concluídos</span>
              <strong>{{ number(summary.tests.completed) }}</strong>
            </div>
            <div class="clinical-row">
              <span>Prescrições emitidas</span>
              <strong>{{ number(summary.prescriptions.value) }}</strong>
            </div>
            <div class="clinical-row">
              <span>Convênios cadastrados</span>
              <strong>{{ number(summary.insurances.value) }}</strong>
            </div>
            <div class="clinical-row">
              <span>Taxa de finalização</span>
              <strong>{{ number(summary.completionRate) }}%</strong>
            </div>
          </div>
        </section>

        <section class="dashboard-panel">
          <div class="panel-heading">
            <div>
              <h2>Leituras rápidas</h2>
              <p>Pontos que merecem atenção</p>
            </div>
          </div>

          <div class="insight-list">
            <article v-for="item in insights" :key="item.title" class="insight-item" :class="`tone-${item.tone}`">
              <strong>{{ item.title }}</strong>
              <p>{{ item.description }}</p>
            </article>
          </div>
        </section>
      </aside>
    </div>
  </section>
</template>

<script>
import axios from "axios";

const emptyMetric = () => ({ value: 0, previous: 0, variation: 0 });

export default {
  name: "Home",

  data: () => ({
    loading: false,
    error: null,
    filters: {
      period: "30d",
      startDate: "",
      endDate: "",
    },
    periodOptions: [
      { label: "7 dias", value: "7d", days: 7 },
      { label: "30 dias", value: "30d", days: 30 },
      { label: "90 dias", value: "90d", days: 90 },
    ],
    summary: {
      appointments: emptyMetric(),
      patients: emptyMetric(),
      tests: { ...emptyMetric(), pending: 0, completed: 0 },
      prescriptions: emptyMetric(),
      insurances: emptyMetric(),
      procedures: emptyMetric(),
      completionRate: 0,
      cancellationRate: 0,
    },
    charts: {
      appointmentsByDay: [],
      patientsByDay: [],
      appointmentsByStatus: [],
      topProcedures: [],
    },
    upcoming: [],
    insights: [],
  }),

  computed: {
    rangeLabel() {
      if (!this.filters.startDate || !this.filters.endDate) return "Indicadores operacionais da clínica";
      return `${this.formatDate(this.filters.startDate)} até ${this.formatDate(this.filters.endDate)}`;
    },

    totalAppointments() {
      return this.number(this.summary.appointments.value);
    },

    hasMovementData() {
      return [...(this.charts.appointmentsByDay || []), ...(this.charts.patientsByDay || [])]
        .some((item) => Number(item.total || 0) > 0);
    },

    hasStatusData() {
      return (this.charts.appointmentsByStatus || []).some((item) => Number(item.total || 0) > 0);
    },

    hasProceduresData() {
      return (this.charts.topProcedures || []).some((item) => Number(item.total || 0) > 0);
    },

    metrics() {
      return [
        {
          key: "appointments",
          label: "Consultas no período",
          value: this.number(this.summary.appointments.value),
          variation: this.summary.appointments.variation,
          icon: "mdi-calendar-check-outline",
          tone: "primary",
        },
        {
          key: "patients",
          label: "Novos pacientes",
          value: this.number(this.summary.patients.value),
          variation: this.summary.patients.variation,
          icon: "mdi-account-plus-outline",
          tone: "success",
        },
        {
          key: "tests",
          label: "Exames solicitados",
          value: this.number(this.summary.tests.value),
          variation: this.summary.tests.variation,
          icon: "mdi-flask-outline",
          tone: "warning",
        },
        {
          key: "prescriptions",
          label: "Prescrições",
          value: this.number(this.summary.prescriptions.value),
          variation: this.summary.prescriptions.variation,
          icon: "mdi-file-document-outline",
          tone: "info",
        },
      ];
    },

    movementChartOptions() {
      const appointments = this.charts.appointmentsByDay || [];
      const patients = this.charts.patientsByDay || [];
      return this.baseChart({
        chart: { type: "areaspline", height: 286 },
        xAxis: { categories: appointments.map((item) => item.label) },
        yAxis: { title: null, allowDecimals: false },
        tooltip: { shared: true },
        series: [
          {
            name: "Consultas",
            type: "areaspline",
            data: appointments.map((item) => item.total),
            color: "#8f52d1",
            fillColor: {
              linearGradient: { x1: 0, y1: 0, x2: 0, y2: 1 },
              stops: [[0, "rgba(143,82,209,.24)"], [1, "rgba(143,82,209,0)"]],
            },
          },
          {
            name: "Pacientes",
            type: "spline",
            data: patients.map((item) => item.total),
            color: "#0f9f8f",
          },
        ],
      });
    },

    statusChartOptions() {
      const data = this.charts.appointmentsByStatus || [];
      return this.baseChart({
        chart: { type: "pie", height: 230 },
        plotOptions: {
          pie: {
            innerSize: "64%",
            borderWidth: 0,
            dataLabels: { enabled: true, distance: 18, style: { fontSize: "11px", textOutline: "none" } },
          },
        },
        series: [{
          name: "Consultas",
          data: data.map((item) => ({ name: item.label, y: item.total })),
          colors: ["#8f52d1", "#0f9f8f", "#d95478", "#a1a8b3"],
        }],
      });
    },

    proceduresChartOptions() {
      const data = this.charts.topProcedures || [];
      return this.baseChart({
        chart: { type: "bar", height: 238 },
        xAxis: { categories: data.map((item) => item.name) },
        yAxis: { title: null, allowDecimals: false },
        plotOptions: { series: { borderRadius: 6, color: "#8f52d1" } },
        series: [{ name: "Consultas", data: data.map((item) => item.total) }],
      });
    },
  },

  created() {
    this.selectPeriod("30d", false);
    this.loadDashboard();
  },

  methods: {
    async loadDashboard() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get("/dashboard", {
          params: {
            startDate: this.filters.startDate,
            endDate: this.filters.endDate,
          },
        });
        const data = response.data.data || {};
        this.summary = { ...this.summary, ...(data.summary || {}) };
        this.charts = { ...this.charts, ...(data.charts || {}) };
        this.upcoming = data.upcoming || [];
        this.insights = data.insights || [];
      } catch (error) {
        await this.loadFallbackDashboard(error);
      } finally {
        this.loading = false;
      }
    },

    async loadFallbackDashboard(sourceError) {
      try {
        const [appointmentsRes, patientsRes, testsRes, prescriptionsRes, insurancesRes, proceduresRes] = await Promise.allSettled([
          axios.get("/appointments", { params: { start: 0, limit: 500 } }),
          axios.get("/patients", { params: { start: 0, limit: 500 } }),
          axios.get("/tests", { params: { start: 0, limit: 500 } }),
          axios.get("/prescriptions", { params: { start: 0, limit: 500 } }),
          axios.get("/insurance", { params: { start: 0, limit: 500 } }),
          axios.get("/procedures", { params: { start: 0, limit: 500 } }),
        ]);

        const appointments = this.settledData(appointmentsRes).filter((item) => this.inRange(item.scheduledDate || item.date));
        const patients = this.settledData(patientsRes).filter((item) => this.inRange(item.date));
        const tests = this.settledData(testsRes).filter((item) => this.inRange(item.date));
        const prescriptions = this.settledData(prescriptionsRes).filter((item) => this.inRange(item.date));
        const insurancesCount = this.settledCount(insurancesRes);
        const proceduresCount = this.settledCount(proceduresRes);

        const statusMap = appointments.reduce((acc, item) => {
          const state = Number(item.state || 0);
          acc[state] = (acc[state] || 0) + 1;
          return acc;
        }, {});

        const procedureMap = appointments.reduce((acc, item) => {
          const name = item.procedure && item.procedure.name ? item.procedure.name : "Sem procedimento";
          acc[name] = (acc[name] || 0) + 1;
          return acc;
        }, {});

        const completedTests = tests.filter((item) => Number(item.status) === 2).length;
        const pendingTests = tests.length - completedTests;

        this.summary = {
          appointments: { value: appointments.length, previous: 0, variation: appointments.length ? 100 : 0 },
          patients: { value: patients.length, previous: 0, variation: patients.length ? 100 : 0 },
          tests: { value: tests.length, previous: 0, variation: tests.length ? 100 : 0, pending: pendingTests, completed: completedTests },
          prescriptions: { value: prescriptions.length, previous: 0, variation: prescriptions.length ? 100 : 0 },
          insurances: { value: insurancesCount, previous: 0, variation: 0 },
          procedures: { value: proceduresCount, previous: 0, variation: 0 },
          completionRate: appointments.length ? Math.round(((statusMap[10] || 0) / appointments.length) * 100) : 0,
          cancellationRate: appointments.length ? Math.round(((statusMap[11] || 0) / appointments.length) * 100) : 0,
        };

        this.charts = {
          appointmentsByDay: this.localDailySeries(appointments, "scheduledDate"),
          patientsByDay: this.localDailySeries(patients, "date"),
          appointmentsByStatus: Object.entries(statusMap).map(([status, total]) => ({
            status: Number(status),
            label: this.appointmentStatusLabel(Number(status)),
            total,
          })),
          topProcedures: Object.entries(procedureMap)
            .map(([name, total]) => ({ name, total }))
            .sort((a, b) => b.total - a.total)
            .slice(0, 5),
        };

        this.upcoming = appointments
          .filter((item) => this.normalizeDate(item.scheduledDate || item.date) >= this.toInputDate(new Date()))
          .sort((a, b) => String(a.scheduledDate || "").localeCompare(String(b.scheduledDate || "")))
          .slice(0, 6)
          .map((item) => ({
            id: item.id,
            state: Number(item.state || 0),
            status: this.appointmentStatusLabel(Number(item.state || 0)),
            scheduledDate: item.scheduledDate || item.date,
            scheduledTime: item.scheduledTime,
            patient: item.patient && item.patient.name ? item.patient.name : "Paciente não informado",
            procedure: item.procedure && item.procedure.name ? item.procedure.name : "Procedimento não informado",
          }));

        this.insights = [
          {
            title: "Agenda",
            description: appointments.length ? `${appointments.length} consulta(s) no período selecionado.` : "Nenhuma consulta no período selecionado.",
            tone: appointments.length ? "success" : "neutral",
          },
          {
            title: "Exames",
            description: pendingTests ? `${pendingTests} exame(s) pendente(s) exigem acompanhamento.` : "Nenhum exame pendente no período.",
            tone: pendingTests ? "warning" : "success",
          },
          {
            title: "Cadastro",
            description: patients.length ? `${patients.length} novo(s) paciente(s) no período.` : "Sem novos pacientes no período selecionado.",
            tone: patients.length ? "primary" : "neutral",
          },
        ];
      } catch (fallbackError) {
        this.error = "Não foi possível carregar os indicadores do dashboard.";
        console.error("Erro ao carregar dashboard:", sourceError || fallbackError);
      }
    },

    selectPeriod(value, reload = true) {
      const option = this.periodOptions.find((item) => item.value === value) || this.periodOptions[1];
      const end = new Date();
      const start = new Date();
      start.setDate(end.getDate() - (option.days - 1));

      this.filters.period = option.value;
      this.filters.startDate = this.toInputDate(start);
      this.filters.endDate = this.toInputDate(end);
      if (reload) this.loadDashboard();
    },

    applyCustomPeriod() {
      this.filters.period = "custom";
      if (this.filters.startDate && this.filters.endDate) this.loadDashboard();
    },

    baseChart(options) {
      return {
        title: null,
        credits: { enabled: false },
        legend: { itemStyle: { color: "#39423d", fontWeight: "600" } },
        chart: {
          backgroundColor: "transparent",
          spacing: [8, 6, 8, 6],
          style: { fontFamily: "Poppins, sans-serif" },
          ...(options.chart || {}),
        },
        xAxis: {
          lineColor: "#e8ece9",
          tickColor: "#e8ece9",
          labels: { style: { color: "#7c8580", fontSize: "11px" } },
          ...(options.xAxis || {}),
        },
        yAxis: {
          gridLineColor: "#edf1ee",
          labels: { style: { color: "#8a938d", fontSize: "11px" } },
          ...(options.yAxis || {}),
        },
        tooltip: {
          borderWidth: 0,
          borderRadius: 10,
          shadow: { color: "rgba(33,20,42,.12)", opacity: 0.12, width: 10 },
          ...(options.tooltip || {}),
        },
        plotOptions: {
          series: { marker: { radius: 4 }, lineWidth: 3 },
          ...(options.plotOptions || {}),
        },
        series: options.series || [],
      };
    },

    number(value) {
      return new Intl.NumberFormat("pt-BR").format(Number(value || 0));
    },

    settledData(result) {
      if (result.status !== "fulfilled") return [];
      return result.value.data.data || [];
    },

    settledCount(result) {
      if (result.status !== "fulfilled") return 0;
      return Number(result.value.data.count || (result.value.data.data || []).length || 0);
    },

    inRange(value) {
      const date = this.normalizeDate(value);
      return !!date && date >= this.filters.startDate && date <= this.filters.endDate;
    },

    normalizeDate(value) {
      if (!value) return "";
      if (typeof value === "string") return value.slice(0, 10);
      return this.toInputDate(new Date(value));
    },

    localDailySeries(items, field) {
      const map = items.reduce((acc, item) => {
        const key = this.normalizeDate(item[field] || item.date);
        if (key) acc[key] = (acc[key] || 0) + 1;
        return acc;
      }, {});

      const start = new Date(`${this.filters.startDate}T00:00:00`);
      const end = new Date(`${this.filters.endDate}T00:00:00`);
      const series = [];

      for (const current = new Date(start); current <= end; current.setDate(current.getDate() + 1)) {
        const key = this.toInputDate(current);
        series.push({
          date: key,
          label: new Intl.DateTimeFormat("pt-BR", { day: "2-digit", month: "2-digit" }).format(current),
          total: map[key] || 0,
        });
      }

      return series;
    },

    appointmentStatusLabel(status) {
      const map = {
        2: "Agendado",
        10: "Finalizado",
        11: "Cancelado",
        12: "Confirmado",
      };
      return map[status] || "Outro";
    },

    formatDate(value) {
      if (!value) return "";
      return new Intl.DateTimeFormat("pt-BR").format(new Date(`${value}T00:00:00`));
    },

    day(value) {
      if (!value) return "--";
      return new Intl.DateTimeFormat("pt-BR", { day: "2-digit" }).format(new Date(value));
    },

    month(value) {
      if (!value) return "---";
      return new Intl.DateTimeFormat("pt-BR", { month: "short" }).format(new Date(value)).replace(".", "");
    },

    toInputDate(date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
  },
};
</script>

<style scoped lang="scss">
.nd-dashboard {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px 18px 22px;
}

.nd-dashboard-toolbar {
  align-items: center;
  background: #fff;
  border: 1px solid #e8ece9;
  border-radius: 8px;
  display: flex;
  gap: 16px;
  justify-content: space-between;
  padding: 8px 10px;
}

.dashboard-range {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 210px;
}

.dashboard-range span {
  color: #8a938d;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.dashboard-range strong {
  color: #18251f;
  font-size: 13px;
  font-weight: 800;
}

.nd-dashboard-actions {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-end;
}

.period-switch {
  background: #f3f0f6;
  border: 1px solid #e5dde9;
  border-radius: 8px;
  display: inline-flex;
  padding: 3px;
}

.period-switch button {
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #59635d;
  cursor: pointer;
  font-size: 11px;
  font-weight: 800;
  min-height: 30px;
  padding: 0 11px;
}

.period-switch button.active {
  background: #8f52d1;
  color: #fff;
  box-shadow: 0 10px 24px rgba(143, 82, 209, 0.22);
}

.date-fields {
  display: flex;
  gap: 8px;
}

.date-fields input {
  background: #fff;
  border: 1px solid #dfe5e0;
  border-radius: 8px;
  color: #243028;
  font-size: 11px;
  font-weight: 700;
  height: 36px;
  padding: 0 12px;
}

.refresh-btn {
  border-radius: 8px;
  height: 36px !important;
}

.metric-grid {
  display: grid;
  gap: 10px;
  grid-template-columns: repeat(4, minmax(0, 1fr));
}

.metric-card {
  align-items: center;
  background: #fff;
  border: 1px solid #e8ece9;
  border-radius: 8px;
  box-shadow: 0 10px 26px rgba(33, 20, 42, 0.04);
  display: flex;
  gap: 10px;
  min-height: 80px;
  padding: 12px;
}

.metric-card__icon {
  align-items: center;
  border-radius: 8px;
  color: #fff;
  display: inline-flex;
  flex: 0 0 36px;
  height: 36px;
  justify-content: center;
  width: 36px;
}

.metric-card__icon.tone-primary { background: #8f52d1; }
.metric-card__icon.tone-success { background: #0f9f8f; }
.metric-card__icon.tone-warning { background: #d19a28; }
.metric-card__icon.tone-info { background: #4056b4; }

.metric-card small {
  color: #707b75;
  display: block;
  font-size: 10px;
  font-weight: 700;
}

.metric-card strong {
  color: #18251f;
  display: block;
  font-size: 22px;
  font-weight: 800;
  line-height: 1;
  margin: 5px 0;
}

.metric-card__trend {
  align-items: center;
  display: inline-flex;
  font-size: 10px;
  font-weight: 800;
  gap: 4px;
}

.metric-card__trend.positive { color: #0f8f71; }
.metric-card__trend.negative { color: #d95478; }

.dashboard-loader {
  border-radius: 99px;
}

.dashboard-layout {
  display: grid;
  gap: 10px;
  grid-template-columns: minmax(0, 1fr) 380px;
}

.dashboard-main,
.dashboard-side {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.dashboard-panel {
  background: #fff;
  border: 1px solid #e8ece9;
  border-radius: 8px;
  box-shadow: 0 10px 26px rgba(33, 20, 42, 0.04);
  min-width: 0;
  padding: 12px;
}

.movement-panel {
  min-height: 272px;
}

.panel-heading {
  align-items: flex-start;
  display: flex;
  gap: 14px;
  justify-content: space-between;
  margin-bottom: 8px;
}

.panel-heading h2 {
  color: #18251f;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0;
  margin: 0;
}

.panel-heading p,
.panel-heading span {
  color: #707b75;
  font-size: 11px;
  font-weight: 600;
  margin: 3px 0 0;
}

.panel-link {
  border-radius: 8px;
  font-weight: 800;
}

.chart {
  min-height: 196px;
}

.chart-tall {
  min-height: 220px;
}

.side-chart {
  min-height: 188px;
}

.empty-chart,
.empty-list {
  align-items: center;
  background: #f8f9f8;
  border: 1px dashed #dfe5e0;
  border-radius: 8px;
  color: #7d8781;
  display: flex;
  flex-direction: column;
  gap: 7px;
  justify-content: center;
  min-height: 188px;
  padding: 18px;
  text-align: center;
}

.empty-chart.compact {
  min-height: 134px;
}

.empty-list {
  min-height: 94px;
}

.empty-chart .v-icon,
.empty-list .v-icon {
  color: #8f52d1;
}

.empty-chart strong {
  color: #18251f;
  font-size: 13px;
  font-weight: 800;
}

.empty-chart span,
.empty-list span {
  color: #7d8781;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  max-width: 320px;
}

.clinical-list,
.insight-list,
.appointment-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.clinical-row {
  align-items: center;
  background: #f7f8f7;
  border: 1px solid #edf1ee;
  border-radius: 8px;
  display: flex;
  justify-content: space-between;
  min-height: 38px;
  padding: 0 10px;
}

.clinical-row span {
  color: #66716b;
  font-size: 11px;
  font-weight: 700;
}

.clinical-row strong {
  color: #18251f;
  font-size: 16px;
  font-weight: 800;
}

.appointment-row {
  align-items: center;
  background: #fff;
  border: 1px solid #e8ece9;
  border-radius: 8px;
  cursor: pointer;
  display: grid;
  gap: 12px;
  grid-template-columns: 58px minmax(0, 1fr) auto;
  min-height: 62px;
  padding: 8px 10px;
  text-align: left;
}

.appointment-row:hover {
  border-color: rgba(143, 82, 209, 0.45);
  box-shadow: 0 12px 28px rgba(143, 82, 209, 0.09);
}

.appointment-date {
  align-items: center;
  background: #f5ecfa;
  border-radius: 8px;
  color: #8f52d1;
  display: flex;
  flex-direction: column;
  height: 46px;
  justify-content: center;
}

.appointment-date strong {
  font-size: 18px;
  font-weight: 800;
  line-height: 1;
}

.appointment-date small {
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
}

.appointment-main strong {
  color: #18251f;
  display: block;
  font-size: 13px;
  font-weight: 800;
}

.appointment-main small {
  color: #707b75;
  display: block;
  font-size: 12px;
  font-weight: 600;
  margin-top: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.appointment-status {
  background: #effaf7;
  border-radius: 999px;
  color: #0f8f71;
  font-size: 11px;
  font-weight: 800;
  padding: 6px 10px;
  white-space: nowrap;
}

.insight-item {
  border-left: 4px solid #dfe5e0;
  border-radius: 8px;
  background: #f7f8f7;
  padding: 10px 12px;
}

.insight-item.tone-primary { border-left-color: #8f52d1; }
.insight-item.tone-success { border-left-color: #0f9f8f; }
.insight-item.tone-warning { border-left-color: #d19a28; }

.insight-item strong {
  color: #18251f;
  display: block;
  font-size: 13px;
  font-weight: 800;
}

.insight-item p {
  color: #66716b;
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  margin: 5px 0 0;
}

@media (max-width: 1280px) {
  .metric-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .dashboard-layout {
    grid-template-columns: 1fr;
  }

  .dashboard-side {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 860px) {
  .nd-dashboard-toolbar,
  .nd-dashboard-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .metric-grid,
  .dashboard-layout,
  .dashboard-side {
    grid-template-columns: 1fr;
  }

  .dashboard-side {
    display: flex;
  }

  .date-fields {
    flex-direction: column;
  }
}
</style>
